import * as Schema from "effect/Schema";

/**
 * The one reasoning word in the cockpit register: the
 * AI SDK v7 set plus "max". Vendor words (`effort`, `reasoningEffort`,
 * `variant`, ACP config ids) stay adapter-local and are mapped at the
 * adapter boundary — never stored in this vocabulary, never compared against
 * it directly (DL1).
 *
 * Thinking on/off is `none` vs a level, not a separate boolean, except where
 * a driver exposes a real toggle.
 */
export const ReasoningLevel = Schema.Literals([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]);
export type ReasoningLevel = typeof ReasoningLevel.Type;

/** Ascending order of the D6 vocabulary; the clamp law reads distance off it. */
export const REASONING_LEVELS: ReadonlyArray<ReasoningLevel> = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

export const isReasoningLevel = Schema.is(ReasoningLevel);

/**
 * Clamp a requested level to the nearest supported one — D6's dispatch-time
 * computation (Claude Code's own rule). Prefers the nearest supported level
 * at or below the request; a request below every supported level takes the
 * lowest supported one. Returns null only when `supported` is empty.
 *
 * DL4: this is computed at dispatch or render time, never stored as a
 * corrected value — the requested level is persisted verbatim beside the
 * runtime-reported applied level, never merged.
 */
export function clampReasoningLevelToSupported(
  requested: ReasoningLevel,
  supported: ReadonlyArray<ReasoningLevel>,
): ReasoningLevel | null {
  if (supported.length === 0) {
    return null;
  }
  const order = (level: ReasoningLevel): number => REASONING_LEVELS.indexOf(level);
  const requestedOrder = order(requested);
  const sorted = [...supported].sort((a, b) => order(a) - order(b));
  let best: ReasoningLevel | null = null;
  for (const level of sorted) {
    if (order(level) <= requestedOrder) {
      best = level;
    }
  }
  return best ?? sorted[0] ?? null;
}
